import {PromptTarget} from '../state/useNovaPromptState';

export type NovaVariableScope = 'TASK' | 'PROJECT_CONTEXT' | 'FILES';
export type NovaPromptInput = 'SYSTEM_PROMPT' | 'USER_PROMPT_TEMPLATE' | 'AGENT_MD';
export type NovaVariablePrecedenceTier = 'INSTRUCTION' | 'PULLED_DATA';
export type NovaVariableLifecycleState = 'ACTIVE' | 'DEPRECATED' | 'REMOVED';

// The fields the library UI actually consumes. The registry serves more metadata (bound condition,
// visibility prose, volume handling, precedence tier, …) but none of it is shown, so it is not
// requested — keeping the fetched shape, and the non-null surface it has to be validated against,
// to only what is used.
export interface NovaVariable {
	token: string;
	scope: NovaVariableScope;
	description: string;
	lifecycleState: NovaVariableLifecycleState;
	permittedInputs: NovaPromptInput[];
}

// Validators for the enum strings the registry returns. The registry is an externally-owned service,
// so a value outside the frozen set (e.g. a lifecycle state added upstream) is possible; these let
// the mapper drop or default such a value instead of trusting it blindly.
export function isNovaVariableScope(value: unknown): value is NovaVariableScope {
	return value === 'TASK' || value === 'PROJECT_CONTEXT' || value === 'FILES';
}

export function isNovaPromptInput(value: unknown): value is NovaPromptInput {
	return value === 'SYSTEM_PROMPT' || value === 'USER_PROMPT_TEMPLATE' || value === 'AGENT_MD';
}

export function isNovaVariableLifecycleState(value: unknown): value is NovaVariableLifecycleState {
	return value === 'ACTIVE' || value === 'DEPRECATED' || value === 'REMOVED';
}

export const PROMPT_TARGET_TO_INPUT: Record<PromptTarget, NovaPromptInput> = {
	[PromptTarget.System]: 'SYSTEM_PROMPT',
	[PromptTarget.User]: 'USER_PROMPT_TEMPLATE',
	[PromptTarget.AgentMd]: 'AGENT_MD',
};

// The registry-backed permission check the shared prompt-state injects. An unknown token is denied
// in every target — the same deny-by-default posture the insertion hook falls back to on its own.
export function buildIsTokenPermitted(variables: NovaVariable[]): (token: string, target: PromptTarget) => boolean {
	const byToken = new Map(variables.map(variable => [variable.token, variable]));
	return (token, target) => {
		const variable = byToken.get(token);
		if (!variable) {
			return false;
		}
		return variable.permittedInputs.includes(PROMPT_TARGET_TO_INPUT[target]);
	};
}

export const SCOPE_ORDER: NovaVariableScope[] = ['TASK', 'PROJECT_CONTEXT', 'FILES'];

export interface NovaVariableGroup {
	scope: NovaVariableScope;
	variables: NovaVariable[];
}

export function groupVariablesByScope(variables: NovaVariable[]): NovaVariableGroup[] {
	return SCOPE_ORDER.map(scope => ({
		scope,
		variables: variables.filter(variable => variable.scope === scope),
	})).filter(group => group.variables.length > 0);
}
